<script>
import api from '@/helpers/api'
import moment from 'moment'
import { sumBy } from 'lodash'
import { authComputed } from '@/state/helpers'
import {
  foamQuantities,
  millQuantities,
  periodCounts,
  metricTrends,
  withDevCounts,
  withDevRows,
  isDashboardDev
} from './dev-data'

const FOAM_TYPES = ['f32', 'rg50', 'rg70', 'ld24', 's4044', 'mix']
const MILL_TYPES = [
  'mill_seat',
  'mill_mattress',
  'mill_std',
  'mill_seat_back',
  'mill_other'
]
const TOP_LIMIT = 3

function quantityFromStat (stat) {
  if (Array.isArray(stat)) return sumBy(stat, 'quantity')
  if (stat && typeof stat === 'object') return Number(stat.quantity) || 0
  return 0
}

export default {
  name: 'dashboard-activity',
  data () {
    return {
      monthCount: 0,
      yearCount: 0,
      monthTrend: 0,
      yearTrend: 0,
      foamRows: [],
      millRows: [],
      foamTotal: 0,
      millTotal: 0,
      isLoading: false
    }
  },
  computed: {
    ...authComputed,
    currentYear () {
      return moment().format('YYYY')
    }
  },
  mounted () {
    this.onRefresh()
  },
  methods: {
    topRows (rows) {
      return [...rows]
        .filter(row => row.quantity > 0)
        .sort((a, b) => b.quantity - a.quantity)
        .slice(0, TOP_LIMIT)
    },
    async onRefresh () {
      this.isLoading = true

      await Promise.all([
        this.loadPeriodCounts(),
        this.loadFoamRows(),
        this.loadMillRows()
      ])

      this.isLoading = false
    },
    percentChange (current, previous) {
      if (!previous) return current > 0 ? 100 : 0
      return Math.round(((current - previous) / previous) * 100)
    },
    async countByPeriod (start, end) {
      const { count } = await api.webForms.countWhere({
        filters: [
          { is_submitted: { _eq: true } },
          { submitted_at: { _gte: start, _lte: end } }
        ]
      })
      return count
    },
    async loadPeriodCounts () {
      const monthStart = moment().startOf('month').toISOString()
      const monthEnd = moment().endOf('month').toISOString()
      const prevMonthStart = moment()
        .subtract(1, 'month')
        .startOf('month')
        .toISOString()
      const prevMonthEnd = moment()
        .subtract(1, 'month')
        .endOf('month')
        .toISOString()

      const yearStart = moment().startOf('year').toISOString()
      const yearEnd = moment().toISOString()
      const prevYearStart = moment()
        .subtract(1, 'year')
        .startOf('year')
        .toISOString()
      const prevYearEnd = moment().subtract(1, 'year').toISOString()

      const [month, prevMonth, year, prevYear] = await Promise.all([
        this.countByPeriod(monthStart, monthEnd),
        this.countByPeriod(prevMonthStart, prevMonthEnd),
        this.countByPeriod(yearStart, yearEnd),
        this.countByPeriod(prevYearStart, prevYearEnd)
      ])

      const periods = withDevCounts(
        { month, year },
        { month: periodCounts.month, year: periodCounts.year }
      )
      const usingFallbackPeriods =
        isDashboardDev() && month === 0 && year === 0

      this.monthCount = periods.month
      this.yearCount = periods.year

      if (usingFallbackPeriods) {
        this.monthTrend = metricTrends.month
        this.yearTrend = metricTrends.year
      } else {
        this.monthTrend = this.percentChange(month, prevMonth)
        this.yearTrend = this.percentChange(year, prevYear)
      }
    },
    async loadFoamRows () {
      const { customer_account_id } = this.userProfile || {}
      const startDate = moment().startOf('year').toDate()
      const endDate = moment().endOf('year').toDate()

      let results = []
      try {
        results = await Promise.all(
          FOAM_TYPES.map(foam =>
            api.itemTagStatPerWeek.fetch({
              start_date: startDate,
              end_date: endDate,
              tags: [`foam_${foam}`],
              customer_id: customer_account_id
            })
          )
        )
      } catch (e) {
        results = FOAM_TYPES.map(() => [])
      }

      const liveFoam = FOAM_TYPES.map((foam, index) => ({
        key: foam,
        label: this.$t(`attribute.foam_type_short.${foam}`),
        quantity: quantityFromStat(results[index])
      }))

      const fallbackFoam = FOAM_TYPES.map((foam, index) => ({
        key: foam,
        label: this.$t(`attribute.foam_type_short.${foam}`),
        quantity: foamQuantities[index] || 0
      }))

      const merged = withDevRows(
        liveFoam.filter(row => row.quantity > 0),
        fallbackFoam.filter(row => row.quantity > 0),
        { key: 'key' }
      )

      this.foamTotal = sumBy(merged, 'quantity')
      this.foamRows = this.topRows(merged)
    },
    async loadMillRows () {
      const { customer_account_id } = this.userProfile || {}
      const startDate = moment().startOf('year').toDate()
      const endDate = moment().endOf('year').toDate()

      let results = []
      try {
        results = await Promise.all(
          MILL_TYPES.map(mill =>
            api.itemTagStatPerWeek.fetch({
              start_date: startDate,
              end_date: endDate,
              tags: [mill],
              customer_id: customer_account_id
            })
          )
        )
      } catch (e) {
        results = MILL_TYPES.map(() => [])
      }

      const liveMill = MILL_TYPES.map((mill, index) => ({
        key: mill,
        label: this.$t(`attribute.mill_type.${mill}`),
        quantity: quantityFromStat(results[index])
      }))

      const fallbackMill = MILL_TYPES.map((mill, index) => ({
        key: mill,
        label: this.$t(`attribute.mill_type.${mill}`),
        quantity: millQuantities[index] || 0
      }))

      const merged = withDevRows(
        liveMill.filter(row => row.quantity > 0),
        fallbackMill.filter(row => row.quantity > 0),
        { key: 'key' }
      )

      this.millTotal = sumBy(merged, 'quantity')
      this.millRows = this.topRows(merged)
    },
    formatTrend (value) {
      const sign = value > 0 ? '+' : ''
      return `${sign}${value}%`
    },
    share (quantity, total) {
      if (!total) return '0%'
      return `${Math.round((quantity / total) * 100)}%`
    },
    goToOrders () {
      this.$router.push({ name: 'sales.my-orders' })
    }
  }
}
</script>

<template>
  <div class="portal-card portal-card--panel h-100">
    <div class="d-flex justify-content-between align-items-start mb-3">
      <div>
        <div class="portal-card__eyebrow mb-1">
          {{ $t('dashboard.activity.title') }}
        </div>
        <div class="portal-card__panel-title">
          {{ $t('dashboard.activity.overview') }}
        </div>
        <p class="text-muted mb-0 small">
          {{ $t('dashboard.activity.subtitle', { year: currentYear }) }}
        </p>
      </div>
      <b-btn
        variant="link"
        size="sm"
        :to="{ name: 'stats.products' }"
        class="p-0 text-nowrap"
      >
        {{ $t('dashboard.activity.view_all_statistics') }}
      </b-btn>
    </div>

    <div class="activity-period mb-3">
      <button type="button" class="activity-period__item" @click="goToOrders">
        <div class="activity-period__value">{{ monthCount }}</div>
        <div class="activity-period__label">
          {{ $t('dashboard.stats.orders_month') }}
        </div>
        <div
          class="activity-period__trend"
          :class="monthTrend >= 0 ? 'is-up' : 'is-down'"
        >
          <i
            :class="
              monthTrend >= 0 ? 'mdi mdi-trending-up' : 'mdi mdi-trending-down'
            "
          ></i>
          {{ formatTrend(monthTrend) }}
          <span>{{ $t('dashboard.stats.vs_last_month') }}</span>
        </div>
      </button>
      <button type="button" class="activity-period__item" @click="goToOrders">
        <div class="activity-period__value">{{ yearCount }}</div>
        <div class="activity-period__label">
          {{ $t('dashboard.stats.orders_year') }}
        </div>
        <div
          class="activity-period__trend"
          :class="yearTrend >= 0 ? 'is-up' : 'is-down'"
        >
          <i
            :class="
              yearTrend >= 0 ? 'mdi mdi-trending-up' : 'mdi mdi-trending-down'
            "
          ></i>
          {{ formatTrend(yearTrend) }}
          <span>{{ $t('dashboard.stats.vs_last_year') }}</span>
        </div>
      </button>
    </div>

    <div v-if="isLoading" class="text-muted small py-2">
      {{ $t('Loading...') }}
    </div>

    <template v-else>
      <div class="activity-table mb-3">
        <div class="activity-table__title">
          {{ $t('dashboard.activity.top_foam') }}
        </div>
        <table v-if="foamRows.length">
          <colgroup>
            <col class="col-type" />
            <col class="col-qty" />
            <col class="col-share" />
          </colgroup>
          <thead>
            <tr>
              <th>{{ $t('dashboard.activity.col_type') }}</th>
              <th class="text-right">{{ $t('quantity') }}</th>
              <th class="text-right">{{ $t('dashboard.activity.col_share') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in foamRows" :key="row.key">
              <td>{{ row.label }}</td>
              <td class="text-right font-weight-bold">{{ row.quantity }}</td>
              <td class="text-right text-muted">
                {{ share(row.quantity, foamTotal) }}
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="text-muted small mb-0">
          {{ $t('dashboard.activity.empty_table') }}
        </p>
      </div>

      <div class="activity-table">
        <div class="activity-table__title">
          {{ $t('dashboard.activity.top_milling') }}
        </div>
        <table v-if="millRows.length">
          <colgroup>
            <col class="col-type" />
            <col class="col-qty" />
            <col class="col-share" />
          </colgroup>
          <thead>
            <tr>
              <th>{{ $t('dashboard.activity.col_type') }}</th>
              <th class="text-right">{{ $t('quantity') }}</th>
              <th class="text-right">{{ $t('dashboard.activity.col_share') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in millRows" :key="row.key">
              <td>{{ row.label }}</td>
              <td class="text-right font-weight-bold">{{ row.quantity }}</td>
              <td class="text-right text-muted">
                {{ share(row.quantity, millTotal) }}
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="text-muted small mb-0">
          {{ $t('dashboard.activity.empty_table') }}
        </p>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.portal-card--panel {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  padding: 1.15rem 1.25rem;
}

.portal-card__eyebrow {
  color: #94a3b8;
  font-size: 0.8rem;
  font-weight: 500;
}

.portal-card__panel-title {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 700;
}

.activity-period {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem;
}

.activity-period__item {
  appearance: none;
  border: 1px solid #eef2f7;
  background: #f8fafc;
  border-radius: 0.75rem;
  padding: 0.75rem 0.85rem;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #fff;
    border-color: #dbe3ef;
  }
}

.activity-period__value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
}

.activity-period__label {
  margin-top: 0.2rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.activity-period__trend {
  margin-top: 0.4rem;
  font-size: 0.72rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.2rem;

  span {
    font-weight: 500;
    color: #94a3b8;
  }

  &.is-up {
    color: #16a34a;
  }

  &.is-down {
    color: #dc2626;
  }
}

.activity-table {
  &__title {
    font-size: 0.82rem;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 0.45rem;
  }

  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    font-size: 0.78rem;
  }

  .col-type {
    width: 56%;
  }

  .col-qty {
    width: 22%;
  }

  .col-share {
    width: 22%;
  }

  th,
  td {
    padding: 0.4rem 0.25rem;
    border-bottom: 1px solid #eef2f7;
    vertical-align: middle;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  th {
    color: #94a3b8;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    font-size: 0.68rem;
  }

  th:nth-child(2),
  th:nth-child(3),
  td:nth-child(2),
  td:nth-child(3) {
    text-align: right;
  }

  tfoot td {
    border-bottom: 0;
    padding-top: 0.55rem;
    color: #0f172a;
  }
}
</style>
